// @ts-nocheck
import { useLocalStorage } from './localstorage';
import { useThemeService } from './theme';

const { navigateToMicroAppClientOnly } = useNavigation();
const { getGigyaUserData, gigyaLogout, getJWT } = useGigyaSession();

export const useAuthService = () => {
  let rurl: string;
  const { userLoggedOut } = useLocalStorage();

  /**
   * Logs out the user by making a POST request to '/api/logout'.
   * Logs out user from gigya, reset the authentication store, setting a flag in local storage,
   * and resetting the theme to default.
   *
   * @returns A Promise that resolves with the response from the server.
   */
  const logOut = async () => {
    const data = (await useFetchHelper().fetchOnCSR('/api/logout', {
      method: 'POST'
    })) as { success: boolean };
    await gigyaLogout();
    if (data['success']) {
      useAuthenticateStore().reset();
      sessionStorage.removeItem(
        useRuntimeConfig().public.sessionStorage['userAccount']
      );
      localStorage.setItem(userLoggedOut, 'true');
      localStorage.setItem(useLocalStorage().theme, 'theme-adult');
    }
    return data;
  };

  const setIsGigyaLoaded = (value: boolean) => {
    useState('isGigyaLoaded').value = value;
  };

  /**
   * Checks the Gigya and C1 session for the current user.
   * If the user is authenticated(Value set from SSR in autheneticateStore) and Gigya is loaded, it retrieves the Gigya user data,
   * performs Gigya and C1 session validation, and loads the user data related to Gigya into the authenticate store.
   * @returns {Promise<boolean>} A promise that resolves to true.
   */
  const checkGigyaAndC1Session = async () => {
    const userData = useAuthenticateStore();
    if (useState('isGigyaLoaded').value) {
      const gigyaData = await getGigyaUserData();
      await gigyaAndC1SessionValidation(gigyaData, userData);
      const user = {
        country: gigyaData?.profile?.country,
        userName: gigyaData?.loginIDs?.username,
        parentUUID: gigyaData?.data?.parentUID,
        loginProvider: gigyaData?.loginProvider
      };
      const authenticateStore = useAuthenticateStore();
      authenticateStore.loadFromSAPCDC(user);
    } else {
      return false;
    }
    return true;
  };

  /**
   * Validates the Gigya and C1 sessions.
   * @param {boolean} gigyaSession - Indicates if the Gigya session exists and it's Data.
   * @param {boolean} c1Session - Indicates if the C1 session exists and it's Data.
   * @returns {Promise<boolean>} - A promise that resolves to `true` if the sessions are valid, or performs necessary actions and redirects otherwise.
   */
  const gigyaAndC1SessionValidation = async (gigyaSession, c1Session) => {
    const route = useRoute();
    rurl = '/dashboard' + route.path;
    if (gigyaSession && c1Session.userId) {
      if (gigyaSession.UID == c1Session.userId) {
        return true;
      } else {
        logOut()
          .then(() => {
            return gigyaLogout();
          })
          .then(() => {
            navigateToRurl();
          });
      }
    } else if (!gigyaSession && c1Session.userId) {
      //end backend session and navigate to home page
      logOut().then(() => {
        // On Logout navigate to home page
        navigateToRurl();
      });
    } else if (gigyaSession && !c1Session.userId) {
      const params = {
        ...gigyaSession,
        jwtToVerify: await getJWT()
      };
      await $fetch('/api/login', {
        method: 'POST',
        body: params
      });

      window.location.reload();
    } else {
      navigateToRurl();
    }
  };

  const navigateToRurl = () => {
    //Navigate to Home if user is unauthenticated
    if (rurl) {
      navigateToMicroAppClientOnly(
        ['login'],
        useRuntimeConfig().public.microapps['urlContracts'].onboarding,
        {
          queryParams: { rurl: encodeURIComponent(rurl) }
        }
      );
    } else {
      navigateToMicroAppClientOnly(
        ['login'],
        useRuntimeConfig().public.microapps['urlContracts'].onboarding
      );
    }
  };

  return {
    setIsGigyaLoaded,
    checkGigyaAndC1Session,
    logOut
  };
};
